// TextRenderingUtils.js
// All text measurement, wrapping, and rendering functions

import { applyShadow, clearShadow } from './CanvasDrawingUtils.js';

// ===== TEXT WRAPPING =====

export const wrapText = (ctx, text, maxWidth) => {
  if (!text) return [];
  
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const metrics = ctx.measureText(testLine);
    
    if (metrics.width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
      
      if (ctx.measureText(word).width > maxWidth) {
        const chars = word.split('');
        let tempWord = '';
        for (const char of chars) {
          if (ctx.measureText(tempWord + char).width > maxWidth) {
            lines.push(tempWord);
            tempWord = char;
          } else {
            tempWord += char;
          }
        }
        currentLine = tempWord;
      }
    } else {
      currentLine = testLine;
    }
  }
  
  if (currentLine) lines.push(currentLine);
  return lines;
};

// ===== 4-TIER FONT SIZE SYSTEM =====

// Global tier definitions (imported from slideTemplates.json)
let GLOBAL_TIERS = null;

export const setGlobalTiers = (tiers) => {
  GLOBAL_TIERS = tiers;
};

// Measure content and select appropriate tier
// Measure content and select appropriate tier (NOW WITH 7 TIERS!)
export const selectTierForContent = (ctx, slideContent, template) => {
  // Quick measurement of content size
  let bulletCount = slideContent.bulletPoints ? slideContent.bulletPoints.length : 0;
  let totalWords = 0;
  
  if (slideContent.heading) {
    totalWords += slideContent.heading.split(' ').length;
  }
  if (slideContent.subheading) {
    totalWords += slideContent.subheading.split(' ').length;
  }
  if (slideContent.bulletPoints) {
    slideContent.bulletPoints.forEach(bullet => {
      totalWords += bullet.split(' ').length;
    });
  }
  
  // Calculate content weight
  const avgWordsPerBullet = bulletCount > 0 ? totalWords / bulletCount : 0;
  const contentWeight = (bulletCount * 1.5) + (avgWordsPerBullet * 0.3);
  
  // Check if template has images (less space for text)
  const hasImage = template.supportsImage && template.image;
  
  // ✅ NEW: 7-Tier thresholds with image adjustment
  const tier1Max = hasImage ? 2 : 3;    // Title slides, minimal content
  const tier2Max = hasImage ? 4 : 5;    // Light content (2-3 bullets)
  const tier3Max = hasImage ? 6 : 7;    // Standard content (4-5 bullets)
  const tier4Max = hasImage ? 8 : 10;   // Medium content (6-7 bullets)
  const tier5Max = hasImage ? 11 : 13;  // Compact content (8-9 bullets)
  const tier6Max = hasImage ? 14 : 16;  // Dense content (10-11 bullets)
  // tier7: Everything else (12+ bullets)
  
  // Select tier based on content weight
  if (bulletCount === 0 || contentWeight < tier1Max) {
    return 'tier1'; // Spacious - 90px
  } else if (contentWeight < tier2Max) {
    return 'tier2'; // Light - 78px
  } else if (contentWeight < tier3Max) {
    return 'tier3'; // Standard - 68px
  } else if (contentWeight < tier4Max) {
    return 'tier4'; // Medium - 58px
  } else if (contentWeight < tier5Max) {
    return 'tier5'; // Compact - 50px
  } else if (contentWeight < tier6Max) {
    return 'tier6'; // Dense - 44px
  } else {
    return 'tier7'; // Maximum - 38px
  }
};

// Apply tier to template
export const applyTierToTemplate = (template, tier, globalTiers) => {
  if (!globalTiers || !globalTiers[tier]) {
  
    return template;
  }
  
  const tieredTemplate = JSON.parse(JSON.stringify(template));
  const tierConfig = globalTiers[tier];
  
  // Apply heading tier
  if (tieredTemplate.heading && tierConfig.heading) {
    const fontFamily = tieredTemplate.heading.font.match(/['"]([^'"]+)['"]/)?.[1] || 
                       tieredTemplate.heading.font.split(' ').pop();
    const fontWeight = tieredTemplate.heading.font.match(/\d+|bold|normal/)?.[0] || 'bold';
    
    tieredTemplate.heading.font = `${fontWeight} ${tierConfig.heading.size}px '${fontFamily}', sans-serif`;
    tieredTemplate.heading.lineHeight = tierConfig.heading.lineHeight;
  }
  
  // Apply subheading tier
  if (tieredTemplate.subheading && tierConfig.subheading) {
    const fontFamily = tieredTemplate.subheading.font.match(/['"]([^'"]+)['"]/)?.[1] || 
                       tieredTemplate.subheading.font.split(' ').pop();
    const fontWeight = tieredTemplate.subheading.font.match(/\d+|bold|normal/)?.[0] || 'bold';
    
    tieredTemplate.subheading.font = `${fontWeight} ${tierConfig.subheading.size}px '${fontFamily}', sans-serif`;
    tieredTemplate.subheading.lineHeight = tierConfig.subheading.lineHeight;
  }
  
  // Apply bullet points tier
  if (tieredTemplate.bulletPoints && tierConfig.bulletPoints) {
    const fontFamily = tieredTemplate.bulletPoints.font.match(/['"]([^'"]+)['"]/)?.[1] || 
                       tieredTemplate.bulletPoints.font.split(' ').pop();
    const fontWeight = tieredTemplate.bulletPoints.font.match(/\d+|bold|normal/)?.[0] || 'bold';
    
    tieredTemplate.bulletPoints.font = `${fontWeight} ${tierConfig.bulletPoints.size}px '${fontFamily}', sans-serif`;
    tieredTemplate.bulletPoints.lineHeight = tierConfig.bulletPoints.lineHeight;
    
    // Update bullet icon size
    const bulletFontFamily = tieredTemplate.bulletPoints.bulletFont.split(' ').pop();
    const bulletFontWeight = tieredTemplate.bulletPoints.bulletFont.match(/\d+|bold|normal/)?.[0] || 'bold';
    tieredTemplate.bulletPoints.bulletFont = `${bulletFontWeight} ${tierConfig.bulletPoints.bulletSize}px ${bulletFontFamily}`;
  }
  
  return tieredTemplate;
};

// Main function to get tiered template (replaces old adjustFontSizes)
export const getTieredTemplate = (ctx, slideContent, template, globalTiers) => {
  const selectedTier = selectTierForContent(ctx, slideContent, template);
  
  
  return applyTierToTemplate(template, selectedTier, globalTiers);
};

// ===== LEGACY FUNCTION (KEPT FOR BACKWARDS COMPATIBILITY) =====
// This is the OLD dynamic scaling - now replaced by tiers
// Keeping it here in case something breaks, we can quickly revert
export const adjustFontSizes = (template, contentHeight, canvasHeight) => {
  
  
  if (contentHeight <= canvasHeight) return template;
  
  const scaleFactor = Math.max(0.6, canvasHeight / contentHeight);
  const adjustedTemplate = JSON.parse(JSON.stringify(template));
  
  if (adjustedTemplate.heading) {
    const originalSize = parseInt(adjustedTemplate.heading.font);
    const newSize = Math.floor(originalSize * scaleFactor);
    adjustedTemplate.heading.font = adjustedTemplate.heading.font.replace(/\d+px/, `${newSize}px`);
    adjustedTemplate.heading.lineHeight = Math.floor(adjustedTemplate.heading.lineHeight * scaleFactor);
  }
  
  if (adjustedTemplate.bulletPoints) {
    const originalSize = parseInt(adjustedTemplate.bulletPoints.font);
    const newSize = Math.floor(originalSize * scaleFactor);
    adjustedTemplate.bulletPoints.font = adjustedTemplate.bulletPoints.font.replace(/\d+px/, `${newSize}px`);
    adjustedTemplate.bulletPoints.lineHeight = Math.floor(adjustedTemplate.bulletPoints.lineHeight * scaleFactor);
    
    const bulletSize = parseInt(adjustedTemplate.bulletPoints.bulletFont);
    const newBulletSize = Math.floor(bulletSize * scaleFactor);
    adjustedTemplate.bulletPoints.bulletFont = adjustedTemplate.bulletPoints.bulletFont.replace(/\d+px/, `${newBulletSize}px`);
  }
  
  if (adjustedTemplate.subheading) {
    const originalSize = parseInt(adjustedTemplate.subheading.font);
    const newSize = Math.floor(originalSize * scaleFactor);
    adjustedTemplate.subheading.font = adjustedTemplate.subheading.font.replace(/\d+px/, `${newSize}px`);
    adjustedTemplate.subheading.lineHeight = Math.floor(adjustedTemplate.subheading.lineHeight * scaleFactor);
  }
  
  return adjustedTemplate;
};

// ===== CONTENT HEIGHT CALCULATION (Still useful for debugging) =====

export const calculateContentHeight = (ctx, slideContent, template) => {
  let totalHeight = 0;
  const topMargin = 100;
  const bottomMargin = 100;
  
  if (template.heading && slideContent.heading) {
    ctx.font = template.heading.font;
    const headingLines = wrapText(ctx, slideContent.heading, template.heading.maxWidth);
    totalHeight += template.heading.y + (headingLines.length * template.heading.lineHeight) + 60;
  }
  
  if (template.subheading && slideContent.subheading) {
    ctx.font = template.subheading.font;
    const subLines = wrapText(ctx, slideContent.subheading, template.subheading.maxWidth);
    totalHeight += (subLines.length * template.subheading.lineHeight) + 40;
  }
  
  if (template.bulletPoints && slideContent.bulletPoints) {
    ctx.font = template.bulletPoints.font;
    slideContent.bulletPoints.forEach(point => {
      const lines = wrapText(ctx, point, template.bulletPoints.maxWidth - template.bulletPoints.indent);
      totalHeight += (lines.length * template.bulletPoints.lineHeight) + 25;
    });
  }
  
  return totalHeight + topMargin + bottomMargin;
};

// ===== TEXT DRAWING =====

export const drawText = (ctx, text, config) => {
  if (!text) return 0;
  
  let displayText = text;
  if (config.textTransform === 'uppercase') {
    displayText = text.toUpperCase();
  } else if (config.textTransform === 'lowercase') {
    displayText = text.toLowerCase();
  }
  
  ctx.font = config.font;
  ctx.fillStyle = config.color;
  ctx.textAlign = config.align;
  ctx.textBaseline = 'top';
  
  if (config.glow) {
    const lines = wrapText(ctx, displayText, config.maxWidth);
    
    ctx.save();
    const layers = 3;
    for (let i = layers; i > 0; i--) {
      ctx.shadowColor = config.glow.color;
      ctx.shadowBlur = (config.glow.blur || 20) * (i / layers);
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      
      lines.forEach((line, idx) => {
        ctx.fillText(line, config.x, config.y + (idx * config.lineHeight));
      });
    }
    ctx.restore();
    
    clearShadow(ctx);
    lines.forEach((line, idx) => {
      ctx.fillText(line, config.x, config.y + (idx * config.lineHeight));
    });
    
    return lines.length * config.lineHeight;
  } else {
    applyShadow(ctx, config.shadow);
    
    const lines = wrapText(ctx, displayText, config.maxWidth);
    
    lines.forEach((line, i) => {
      ctx.fillText(line, config.x, config.y + (i * config.lineHeight));
    });
    
    clearShadow(ctx);
    
    return lines.length * config.lineHeight;
  }
};

// ===== BULLET POINTS DRAWING =====

export const drawBulletPoints = (ctx, bulletPoints, config) => {
  if (!bulletPoints || bulletPoints.length === 0) return;
  
  let currentY = config.y;
  
  bulletPoints.forEach((point, index) => {
    ctx.font = config.bulletFont;
    ctx.fillStyle = config.bulletColor;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    
    if (config.glow) {
      ctx.save();
      const layers = 2;
      for (let i = layers; i > 0; i--) {
        ctx.shadowColor = config.glow.color;
        ctx.shadowBlur = (config.glow.blur || 10) * (i / layers);
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
        ctx.fillText(config.bulletStyle, config.x, currentY);
      }
      ctx.restore();
      clearShadow(ctx);
      ctx.fillText(config.bulletStyle, config.x, currentY);
    } else {
      applyShadow(ctx, config.shadow);
      ctx.fillText(config.bulletStyle, config.x, currentY);
      clearShadow(ctx);
    }
    
    ctx.font = config.font;
    ctx.fillStyle = config.color;
    applyShadow(ctx, config.shadow);
    
    const lines = wrapText(ctx, point, config.maxWidth - config.indent);
    
    lines.forEach((line) => {
      ctx.fillText(line, config.x + config.indent, currentY);
      currentY += config.lineHeight;
    });
    
    clearShadow(ctx);
    
    if (index < bulletPoints.length - 1) {
      currentY += 25;
    }
  });
  
  return currentY;
};